// decrypt-helpers.js — Wire-to-dashboard mapping helpers for the
// HMAC-encrypted fields that the coordinator's /v1/sync/state +
// /v1/teams/events return.
//
// The coordinator stores Resource as a HMAC-hashed string
// ("file:h:<hex>:h:<hex>"), but every lease + event also carries an
// AES-GCM-encrypted display_hint (or payload_hint) whose plaintext is
// the repo-relative file path (encryptDisplayHint in daemon's
// teamsync.go). These helpers decrypt those hints client-side so chat
// labels, the EDITING IN bar, and RECENT EDITS render real paths —
// exactly what the local daemon dashboard shows by default. When no
// key is loaded or decryption fails, they fall back to the hashed
// values so the UI degrades gracefully rather than going blank.
//
// All four are pure functions over (input, state.hmacKey). They're
// called from bootstrap's fetchAll + refreshLeaseData on every poll
// tick, so they need to be cheap; the heavy work (AES-GCM decrypt)
// is delegated to crypto.js.
//
// Exports:
//   buildDeviceLabelMap(heartbeats, prev)   — merge decrypted device
//                                              labels into a stable map
//   decryptLeaseRow(l)                      — wire-format lease →
//                                              dashboard-shape with
//                                              decrypted Resource + Purpose
//   decryptEventRow(e)                      — wire-format event →
//                                              dashboard-shape with
//                                              decrypted Payload + Resource
//   mergeLeaseHistoryFromEvents(held, evs)  — synthesize released/
//                                              expired/blocked rows
//                                              from the event stream
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2s).
import { state } from "./state.js";
import { purposeToPath, looksLikeHashedResource } from "./format.js";
import { decryptHint } from "./crypto.js";
import { parseUTC } from "./util.js";

// buildDeviceLabelMap merges newly-decrypted heartbeat device labels
// into a stable map. We carry forward the previous map's entries so
// a chat whose laptop didn't appear in the latest heartbeat batch
// (e.g. went idle) still renders its last-known label rather than
// flickering back to "?".
export function buildDeviceLabelMap(heartbeats, prev) {
  const m = Object.assign({}, prev || {});
  for (const hb of heartbeats) {
    if (hb.laptop_id && hb.deviceLabel) {
      m[hb.laptop_id] = hb.deviceLabel;
    }
  }
  return m;
}

// Map a wire-format lease (from /v1/sync/state) to the Resource-shape
// the dashboard renderers expect. The coordinator stores Resource as
// a HMAC-hashed string ("file:h:<hex>:h:<hex>"), but every lease also
// carries an AES-GCM-encrypted display_hint whose plaintext is the
// repo-relative file path (encryptDisplayHint in daemon's teamsync.go).
// We decrypt that and use it as the user-facing Resource so chat
// labels, the EDITING IN bar, and RECENT EDITS all render real paths
// — exactly what the local dashboard shows by default. When no key is
// loaded or decryption fails, we fall back to the hashed Resource so
// the UI degrades gracefully rather than going blank.
export async function decryptLeaseRow(l) {
  let resource = l.resource || "";
  let purpose = "";
  if (l.display_hint && state.hmacKey) {
    const plain = await decryptHint(l.display_hint);
    if (plain != null) {
      const nl = plain.indexOf("\n");
      if (nl >= 0) {
        resource = plain.slice(0, nl) || resource;
        purpose = plain.slice(nl + 1);
      } else if (plain) {
        resource = plain;
      }
    }
  }
  // repo_hint (daemon v0.45+) carries the repo label ("owner/repo" or a
  // basename) as its own AES-GCM blob. The daemon strips the repo root
  // out of display_hint at encryption time (privacy), so the decrypted
  // resource is repo-relative and repoFromResource() can't recover the
  // repo name — this dedicated field is what populates the REPO column.
  // Accept both snake_case (raw /v1/sync/state rows) and PascalCase
  // (the /v1/teams aggregated path reshapes to RepoHint). Empty for
  // pre-v0.45 daemon rows; leases.js then falls back to parsing the
  // resource, then to "--".
  let repo = "";
  const repoHint = l.repo_hint || l.RepoHint || "";
  if (repoHint && state.hmacKey) {
    const plain = await decryptHint(repoHint);
    if (plain != null && plain !== "") repo = plain;
  }
  return {
    LeaseID: l.lease_id,
    AgentID: l.agent_id,
    Resource: resource,
    State: l.state,
    AcquiredAt: l.acquired_at,
    ExpiresAt: l.expires_at,
    LastHeartbeatAt: l.last_heartbeat_at,
    Purpose: purpose,
    Repo: repo,
    display_hint: l.display_hint || "",
    repo_hint: repoHint,
    laptop_id: l.laptop_id || "",
  };
}

// decryptRepoForRows resolves the REPO label AND the user-facing Resource
// path for rows that are ALREADY in the dashboard's PascalCase shape (the
// cloud /v1/teams/leases path returns held leases pre-reshaped to
// LeaseID/Resource/State/... + raw RepoHint/DisplayHint blobs, so they
// bypass decryptLeaseRow entirely). Mutating in place is fine — these are
// freshly-built objects owned by the caller.
//
// Two hints, two columns:
//   • RepoHint   → Repo     (the REPO column)
//   • DisplayHint→ Resource (+ Purpose) — the file path. Without this the
//     overview poller leaves Resource as the hashed `file:h:…` wire value,
//     so friendlyResource() returns "" and EVERY consumer of state.leasesAll
//     (Files "encrypted files" count, Chats labels, Leases path/REPO columns,
//     the sparkline) degrades whenever this poller wins the race against
//     bootstrap's decryptLeaseRow path. Mirrors decryptLeaseRow:66-77.
//     Falls back to the hashed wire value when there's no hint or decrypt
//     fails (stale key) — same contract as decryptLeaseRow.
export async function decryptRepoForRows(rows) {
  if (!state.hmacKey) return rows;
  await Promise.all(
    (rows || []).map(async (r) => {
      const repoBlob = r.RepoHint || r.repo_hint || "";
      if (repoBlob) {
        const plain = await decryptHint(repoBlob);
        if (plain != null && plain !== "") r.Repo = plain;
      }
      // Recover the path from DisplayHint only when Resource is still the
      // hashed wire value — a plaintext Resource (daemon path) is left alone.
      const dispBlob = r.DisplayHint || r.display_hint || "";
      if (dispBlob && looksLikeHashedResource(r.Resource || "")) {
        const plain = await decryptHint(dispBlob);
        if (plain != null) {
          const nl = plain.indexOf("\n");
          if (nl >= 0) {
            if (plain.slice(0, nl)) r.Resource = plain.slice(0, nl);
            r.Purpose = plain.slice(nl + 1);
          } else if (plain) {
            r.Resource = plain;
          }
        }
      }
    }),
  );
  return rows;
}

// Map a wire-format coordination event to the dashboard shape,
// decrypting payload_hint into Payload and using
// payload.last_file_path (when present) as the user-facing Resource.
// Mirrors decryptLeaseRow's contract.
export async function decryptEventRow(e) {
  let payload = { held_by: e.held_by || "" };
  // A local daemon sends the payload in the clear; keep it so rows show the
  // branch, holder and reason it carries.
  if (e.payload && typeof e.payload === "object") payload = { ...payload, ...e.payload };
  if (e.payload_hint && state.hmacKey) {
    const plain = await decryptHint(e.payload_hint);
    if (plain) {
      try {
        const parsed = JSON.parse(plain);
        if (parsed && typeof parsed === "object")
          payload = { ...payload, ...parsed };
      } catch {}
    }
  }
  // Resolve the user-facing Resource from the sources that name a file, in
  // order. The purpose is a last resort and only counts in the shapes that
  // wrap a path ("Editing <path>"): read loosely it put a chat's task text
  // in the file column. With none, the hashed wire Resource stays and the
  // row says the file is hidden.
  //   - a local daemon's row: e.resource is the plain "file:<root>:<rel>"
  //   - payload.file_path: daemons from v0.76.4 add it to every file event
  //   - lease.expired (sweeper): payload.last_file_path
  //   - heartbeat-derived events: payload.resource (already plaintext)
  const str = (v) => (typeof v === "string" ? v : "");
  const plainWire = e.resource && !looksLikeHashedResource(e.resource) ? e.resource : "";
  const fromPayloadRes =
    str(payload.resource) && !looksLikeHashedResource(payload.resource) ? payload.resource : "";
  const decryptedResource =
    plainWire ||
    str(payload.file_path) ||
    str(payload.last_file_path) ||
    fromPayloadRes ||
    purposeToPath(str(payload.purpose)) ||
    e.resource ||
    "";
  // The row's time, for telling old unheld-change rows from real ones
  // (format.js isOldUnheldChange); hidden from the payload's own listing.
  Object.defineProperty(payload, "_at", { value: parseUTC(e.occurred_at), enumerable: false });
  return {
    EventType: e.event_type,
    event_type: e.event_type,
    OccurredAt: e.occurred_at,
    occurred_at: e.occurred_at,
    Resource: decryptedResource,
    resource: decryptedResource,
    AgentID: e.agent_id || "",
    agent_id: e.agent_id || "",
    EventID: "c" + e.id,
    event_id: "c" + e.id,
    Payload: payload,
    payload: payload,
  };
}

// mergeLeaseHistoryFromEvents synthesizes released / expired /
// blocked lease rows from the event stream. The coordinator doesn't
// store full lease lifecycle rows — it stores the current held set +
// an event stream. The local daemon UI does the same synthesis from
// its own event log, which is why the Leases-page filter chips work
// there. This brings the hosted dashboard to parity.
const CLOSING_EVENTS = {
  "lease.released": "released",
  "lease.revoked": "revoked",
  "lease.expired": "expired",
  "lease.blocked": "blocked",
};

export function mergeLeaseHistoryFromEvents(heldLeases, events) {
  const result = [...heldLeases];

  // Walk events in chronological order so acquisition sets context
  // for the next close event on the same (agent, resource) pair.
  const evsAsc = [...(events || [])].sort(
    (a, b) =>
      new Date(a.occurred_at || a.OccurredAt).getTime() -
      new Date(b.occurred_at || b.OccurredAt).getTime(),
  );

  // Open lease state per (agent, resource): { acquired_at, held_by }
  const open = new Map();
  for (const ev of evsAsc) {
    const evt = ev.event_type || ev.EventType || "";
    if (!evt.startsWith("lease.")) continue; // skip team.* audit events
    const aid = ev.AgentID || ev.agent_id || "";
    const res = ev.Resource || ev.resource || "";
    if (!aid || !res) continue;
    const key = aid + "|" + res;
    const ts = ev.occurred_at || ev.OccurredAt;
    const payload = ev.Payload || ev.payload || {};

    if (evt === "lease.acquired") {
      open.set(key, { acquired_at: ts, payload });
      continue;
    }
    // Closing events emit a synthetic row. A close always ends an earlier
    // acquisition, so it is history even when the same chat holds the file
    // again now (that hold has its own, still-open acquisition).
    const stateName = CLOSING_EVENTS[evt];
    if (stateName) {
      const opener = open.get(key);
      if (!opener) continue; // no acquisition seen; skip
      result.push({
        LeaseID: "syn:" + (ev.EventID || ev.event_id || ""),
        AgentID: aid,
        Resource: res,
        State: stateName,
        AcquiredAt: opener.acquired_at,
        ExpiresAt: ts,
        LastHeartbeatAt: ts,
        Purpose:
          (opener.payload &&
            (opener.payload.purpose || opener.payload.Purpose)) ||
          (payload && (payload.purpose || payload.Purpose)) ||
          "",
        display_hint: "",
        laptop_id: "",
      });
      open.delete(key);
    }
  }

  // Sort newest-first (by AcquiredAt) so the Leases page render uses
  // the natural ordering.
  result.sort(
    (a, b) =>
      new Date(b.AcquiredAt).getTime() - new Date(a.AcquiredAt).getTime(),
  );
  return result;
}
